import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAppSelector } from 'store/hook';

import { NewSidebar } from './NewSidebar';
import { TopBar } from './TopBar/TopBar';
import { SnackBar } from '../SnackBar/SnackBar';
import { BreadCrumb } from './TopBar/components/BreadCrumb';
import { ChatBotFloatingButton } from 'views/Chatbot/ChatbotFloatingButton';
import useAppTheme from 'hooks/useAppTheme';
import { isButtonEnabled } from './FloatingButtons/utils/validators';
import DeviceHandler from 'handlers/device.handler';
import NotificationHandler from 'handlers/notification.handler';
import HierarchyHandler from 'handlers/hierarchy.handler';
import ExternalUserHandler from 'handlers/external.user.handler';
import {
  CLIENT_THEME,
  clientThemePalette,
  isYokohamaTheme,
} from 'utils/clientTheme';
import { useMachinesInit } from 'hooks/useMachinesInit';
import { useTpmsInit } from 'hooks/useTpmsInit';
import { SingleFadeLoader } from 'components/SingleFadeLoader';

// These calls used to run unconditionally inside the login bootstrap
// (fetch.all.ts's getCommonPromises), but they're consumed broadly across
// many routes and TopBar chrome, not by a single page, so they're triggered
// here instead -- once per authenticated session (AppLayout only
// mounts/unmounts around full navigation into/out of authenticated routes,
// not per page) rather than being tangled into the same synchronous login
// batch as everything else.
function useSessionWideDataFetch() {
  const { isAuthenticated, accessToken, customer } = useAppSelector(
    (state) => state.authReducer
  );
  const isMachinesModule = customer.modules?.isMachinesModule ?? false;
  const { latestSensorPollingId, notificationPollingId } = useAppSelector(
    (state) => state.appReducer
  );
  const pollingIdsRef = useRef({
    latestSensorPollingId,
    notificationPollingId,
  });
  pollingIdsRef.current = { latestSensorPollingId, notificationPollingId };

  // Handler constructors call useAppDispatch() (see BaseHandler), so they
  // must be instantiated during render with useMemo, not inside the effect below.
  const deviceHandler = useMemo(() => new DeviceHandler(), []);
  const notificationHandler = useMemo(() => new NotificationHandler(), []);
  const hierarchyHandler = useMemo(() => new HierarchyHandler(), []);
  const externalUserHandler = useMemo(() => new ExternalUserHandler(), []);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    // getDeviceLatestSensorDataAll also fetches device/operational-data
    // (trip count, utilization, tonnage, engine hours) which is
    // machines-module-only data -- skip it entirely for tires/parts-only
    // customers rather than fetching it on every route regardless of module.
    if (isMachinesModule) {
      void deviceHandler.getDeviceLatestSensorDataAll();
    }
    void notificationHandler.getAllLatest();
    void hierarchyHandler.get();
    void externalUserHandler.getAll();

    return () => {
      const { latestSensorPollingId, notificationPollingId } =
        pollingIdsRef.current;
      if (latestSensorPollingId) clearTimeout(latestSensorPollingId);
      if (notificationPollingId) clearTimeout(notificationPollingId);
    };
  }, [isAuthenticated, accessToken, isMachinesModule]);
}

export const AppLayout = () => {
  useSessionWideDataFetch();

  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { loading: machinesLoading } = useMachinesInit();
  const { loading: tpmsLoading } = useTpmsInit();

  if (machinesLoading || tpmsLoading) {
    return <SingleFadeLoader isFullScreen={true} />;
  }

  return (
    <>
      <TopBar />
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 65px)',
          backgroundColor: enableDarkTheme
            ? palette.pageBackground
            : palette.pageBackground,
        }}
      >
        <NewSidebar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'scroll',
            height: 'calc(100vh - 64px)',
            transition: 'width 0.3s ease-in-out',
            // padding: '2px',
          }}
        >
          <BreadCrumb />
          <Box sx={{ px: 2 }}>
            <Outlet />
          </Box>
          {!isYokohamaTheme() &&
            isButtonEnabled('chatbotButton', customerCode) && (
              <ChatBotFloatingButton />
            )}
          {/* <Box sx={{ flexGrow: 1, overflow: 'scroll' }}>
          </Box> */}
        </Box>
      </Box>
      <SnackBar />
    </>
  );
};
